'use client'

import React, { useState, useRef, useEffect, useCallback } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { ChevronLeft, ChevronRight, ArrowUpRight } from 'lucide-react'
import { motion, useInView, AnimatePresence } from 'framer-motion'
import type { Testimonial, Media } from '@/payload-types'
import { trackTestimonialScrolled, trackCtaClicked } from '../../lib/analytics'
import { getMediaUrl } from '../../lib/media'

interface TestimonialsProps {
  testimonials: Testimonial[]
  tagline?: any
  detail?: any
}

function useItemsPerPage() {
  const [itemsPerPage, setItemsPerPage] = useState(4)

  const update = useCallback(() => {
    const width = window.innerWidth
    if (width < 640) setItemsPerPage(1)
    else if (width < 1024) setItemsPerPage(2)
    else if (width < 1280) setItemsPerPage(3)
    else setItemsPerPage(4)
  }, [])

  useEffect(() => {
    update()
    window.addEventListener('resize', update)
    return () => window.removeEventListener('resize', update)
  }, [update])

  return itemsPerPage
}

export function TestimonialsClient({ testimonials }: TestimonialsProps) {
  const sortedTestimonials = [...testimonials].sort((a, b) => {
    const aWords = (a.shortTestimonial || '').split(/\s+/).filter(Boolean).length
    const bWords = (b.shortTestimonial || '').split(/\s+/).filter(Boolean).length
    return bWords - aWords
  })

  const [currentIndex, setCurrentIndex] = useState(0)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const sectionRef = useRef<HTMLElement>(null)
  const isInView = useInView(sectionRef, { once: true, amount: 0.4 })

  const itemsPerPage = useItemsPerPage()
  const totalPages = Math.ceil(sortedTestimonials.length / itemsPerPage)

  // Keep currentIndex valid when itemsPerPage changes
  useEffect(() => {
    setCurrentIndex((prev) => {
      const maxStart = Math.max(0, sortedTestimonials.length - itemsPerPage)
      return Math.min(prev, maxStart)
    })
  }, [itemsPerPage, sortedTestimonials.length])

  const handlePrevious = () => {
    setCurrentIndex((prev) => {
      const newIndex = prev - itemsPerPage
      const result = newIndex < 0 ? Math.max(0, sortedTestimonials.length - itemsPerPage) : newIndex
      trackTestimonialScrolled({ testimonial_index: result, direction: 'previous' })
      return result
    })
  }

  const handleNext = () => {
    setCurrentIndex((prev) => {
      const newIndex = prev + itemsPerPage
      const result = newIndex >= sortedTestimonials.length ? 0 : newIndex
      trackTestimonialScrolled({ testimonial_index: result, direction: 'next' })
      return result
    })
  }

  if (!sortedTestimonials.length) return null

  return (
    <motion.section
      ref={sectionRef}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
      className="py-16 md:py-24 px-6 md:px-20 overflow-x-hidden"
    >
      <div className="max-w-380 mx-auto">
        {/* Large Quote Mark */}
        <div className="relative">
          <div className="absolute -left-4 md:-left-12 -top-8 md:-top-12 text-brand-gold/40 text-[100px] md:text-[150px] lg:text-[190px] leading-none font-serif -mb-8 md:-mb-12 z-10">
            “
          </div>

          {/* Heading */}
          <h2 className="relative text-3xl md:text-4xl z-20 lg:text-5xl font-medium text-gray-900 mb-8 md:mb-12">
            In their own words
          </h2>
        </div>

        {/* Testimonial Cards Grid */}
        <div
          ref={scrollContainerRef}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8 mb-8 md:mb-12"
        >
          <AnimatePresence mode="popLayout">
            {sortedTestimonials
              .slice(currentIndex, currentIndex + itemsPerPage)
              .map((testimonial, index) => {
                const headshot = testimonial.headshot as Media | null | undefined
                const headshotUrl = getMediaUrl(headshot, 'thumbnail')

                return (
                  <motion.div
                    key={`${currentIndex}-${testimonial.id}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{
                      opacity: 0,
                      y: -20,
                      transition: {
                        delay: index * 0.1,
                        duration: 0.3,
                      },
                    }}
                    transition={{
                      duration: 0.3,
                      delay: 0.2 + index * 0.1,
                    }}
                    className="testimonial-card flex flex-col h-[320px] md:h-[360px]"
                  >
                    {/* Testimonial Text */}
                    <p className="testimonial-text text-base md:text-lg text-gray-800 leading-relaxed mb-4 overflow-y-auto pr-2">
                      {testimonial.shortTestimonial}
                    </p>
                    {/* Client Info */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-start gap-3">
                        {headshotUrl && (
                          <div className="relative w-8 h-8 rounded-full overflow-hidden shrink-0">
                            <Image
                              src={headshotUrl}
                              alt={testimonial.name || ''}
                              fill
                              sizes="32px"
                              className="object-cover w-full h-full border"
                            />
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-gray-900 text-sm md:text-base">
                            {testimonial.name}
                          </p>
                          <p className="text-gray-600 text-xs md:text-sm">{testimonial.title}</p>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )
              })}
          </AnimatePresence>
        </div>

        {/* Bottom Controls */}
        <div className="flex items-center justify-between gap-4">
          <Link
            href="/consultation"
            onClick={() =>
              trackCtaClicked({
                cta_text: 'Book a Consultation',
                cta_location: 'testimonials',
                destination_url: '/consultation',
              })
            }
            className="inline-flex items-center gap-2 px-6 py-3 bg-transparent text-gray-900 rounded-full border border-gray-900 hover:bg-gray-900 hover:text-white transition-all font-medium text-sm md:text-base"
          >
            Book a Consultation
            <ArrowUpRight className="w-4 h-4" />
          </Link>

          {/* Navigation */}
          <div className="flex items-center gap-3">
            {/* Pagination - dots on desktop, counter on mobile */}
            <div className="hidden sm:flex items-center gap-2">
              {Array.from({ length: totalPages }).map((_, pageIndex) => (
                <button
                  key={pageIndex}
                  onClick={() => setCurrentIndex(pageIndex * itemsPerPage)}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    Math.floor(currentIndex / itemsPerPage) === pageIndex
                      ? 'bg-gray-900 w-6'
                      : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                  aria-label={`Go to page ${pageIndex + 1}`}
                />
              ))}
            </div>
            <span className="sm:hidden text-sm text-gray-600 tabular-nums">
              {Math.floor(currentIndex / itemsPerPage) + 1} / {totalPages}
            </span>
            <div className="flex gap-2">
              <button
                onClick={handlePrevious}
                className="w-8 h-8 rounded-full border border-gray-900 bg-transparent flex items-center justify-center hover:bg-gray-900 hover:text-white transition-all"
                aria-label="Previous testimonials"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={handleNext}
                className="w-8 h-8 rounded-full border border-gray-900 bg-transparent flex items-center justify-center hover:bg-gray-900 hover:text-white transition-all"
                aria-label="Next testimonials"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .testimonial-text::-webkit-scrollbar {
          width: 4px;
        }
        .testimonial-text::-webkit-scrollbar-track {
          background: transparent;
        }
        .testimonial-text::-webkit-scrollbar-thumb {
          background: rgba(156, 163, 175, 0.4);
          border-radius: 4px;
        }
        .testimonial-text::-webkit-scrollbar-thumb:hover {
          background: rgba(156, 163, 175, 0.6);
        }
        .testimonial-text {
          scrollbar-width: thin;
          scrollbar-color: rgba(156, 163, 175, 0.2) transparent;
        }
      `}</style>
    </motion.section>
  )
}
